import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Avatar,
  Box,
  Button,
  Card,
  Flex,
  Grid,
  Heading,
  Select,
  Text,
  TextField,
} from "@radix-ui/themes";
import * as Accordion from "@radix-ui/react-accordion";
import { ChevronDown, Image as ImageIcon, Pencil, X } from "lucide-react";
import { userProfileService } from "@/services/breeze/UserProfileService";
import { fileService } from "@/services/breeze/FileService";
import { FilePickerDialog } from "@/components/dashboard/FilePickerDialog";
import { IUserProfile, UpdateUserProfile } from "@/types/breeze/UserProfile";
import { SkeletonText } from "@/components/ui/Skeleton";
import { formatDate } from "@/utils/date";

interface Props {
  userId: number;
}

export default function UserProfileCard({ userId }: Props) {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);

  const {
    data: profile,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["user-profile", userId],
    queryFn: () => userProfileService.getByUser(userId),
    retry: false,
  });

  const updateMutation = useMutation({
    mutationFn: (patch: UpdateUserProfile) =>
      userProfileService.updateByUser(userId, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-profile", userId] });
      setIsEditing(false);
    },
  });

  const summary = profile
    ? populatedFieldSummary(profile)
    : isLoading
      ? "Loading…"
      : "No profile data";

  return (
    <Card>
      <Accordion.Root type="single" collapsible>
        <Accordion.Item value="profile">
          <Accordion.Trigger
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              padding: "var(--space-2)",
              background: "transparent",
              border: "none",
              cursor: "pointer",
            }}
          >
            <Flex align="center" gap="3">
              <Heading size="4" weight="medium" m="0">
                Profile
              </Heading>
              <Text size="2" color="gray">
                {summary}
              </Text>
            </Flex>
            <ChevronDown size={16} />
          </Accordion.Trigger>
          <Accordion.Content>
            <Box p="2" pt="4">
              {isLoading && <SkeletonText lines={6} />}
              {error && (
                <Text size="2" color="gray">
                  Could not load profile — {(error as Error).message}
                </Text>
              )}
              {profile && !isEditing && (
                <ProfileView
                  profile={profile}
                  onEdit={() => setIsEditing(true)}
                />
              )}
              {profile && isEditing && (
                <ProfileEdit
                  profile={profile}
                  saving={updateMutation.isPending}
                  onSave={(patch) => updateMutation.mutate(patch)}
                  onCancel={() => setIsEditing(false)}
                />
              )}
            </Box>
          </Accordion.Content>
        </Accordion.Item>
      </Accordion.Root>
    </Card>
  );
}

function ProfileView({
  profile,
  onEdit,
}: {
  profile: IUserProfile;
  onEdit: () => void;
}) {
  return (
    <Flex direction="column" gap="4">
      <Flex justify="between" align="start" gap="4">
        <Flex align="center" gap="3">
          <AvatarPreview
            fileId={profile.avatar_file_id}
            fallback={profile.preferred_name}
          />
          <Field label="Preferred name" value={profile.preferred_name} />
        </Flex>
        <Button variant="soft" size="1" onClick={onEdit}>
          <Pencil size={14} /> Edit
        </Button>
      </Flex>

      <Grid columns={{ initial: "1", sm: "2" }} gap="4">
        <Field label="Timezone" value={profile.timezone} />
        <Field label="Locale" value={profile.locale} />
        <Field
          label="Marketing consent"
          value={consentLabel(
            profile.marketing_consent,
            profile.marketing_consent_at,
          )}
        />
      </Grid>

      <ReadOnlyGroup label="Attribution">
        <Field label="Source" value={profile.utm_source} inline />
        <Field label="Medium" value={profile.utm_medium} inline />
        <Field label="Campaign" value={profile.utm_campaign} inline />
        <Field label="Term" value={profile.utm_term} inline />
        <Field label="Content" value={profile.utm_content} inline />
        <Field label="Referrer" value={profile.referrer} inline />
      </ReadOnlyGroup>

      <ReadOnlyGroup label="Signup">
        <Field label="Country" value={profile.signup_country} inline />
        <Field label="IP" value={profile.signup_ip} inline />
      </ReadOnlyGroup>
    </Flex>
  );
}

function ProfileEdit({
  profile,
  saving,
  onSave,
  onCancel,
}: {
  profile: IUserProfile;
  saving: boolean;
  onSave: (patch: UpdateUserProfile) => void;
  onCancel: () => void;
}) {
  const [values, setValues] = useState<UpdateUserProfile>(() =>
    editableSubset(profile),
  );

  const set = <K extends keyof UpdateUserProfile>(
    key: K,
    value: UpdateUserProfile[K],
  ) => setValues((prev) => ({ ...prev, [key]: value }));

  return (
    <Flex direction="column" gap="4">
      <Flex align="center" gap="3">
        <AvatarPreview
          fileId={values.avatar_file_id}
          fallback={values.preferred_name}
        />
        <AvatarPicker
          onPick={(fileId) => set("avatar_file_id", fileId)}
          onRemove={() => set("avatar_file_id", null)}
          hasAvatar={values.avatar_file_id !== null}
        />
      </Flex>

      <Grid columns={{ initial: "1", sm: "2" }} gap="4">
        <TextInput
          label="Preferred name"
          value={values.preferred_name}
          onChange={(v) => set("preferred_name", v)}
        />
        <TextInput
          label="Timezone"
          value={values.timezone}
          onChange={(v) => set("timezone", v)}
          placeholder="e.g. Europe/London"
        />
        <TextInput
          label="Locale"
          value={values.locale}
          onChange={(v) => set("locale", v)}
          placeholder="e.g. en-GB"
        />
        <ConsentSelect
          value={values.marketing_consent}
          onChange={(v) => set("marketing_consent", v)}
        />
      </Grid>

      <Flex gap="2" justify="end">
        <Button variant="soft" color="gray" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={() => onSave(values)} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </Flex>
    </Flex>
  );
}

function AvatarPreview({
  fileId,
  fallback,
}: {
  fileId: number | null;
  fallback: string | null;
}) {
  const { data: url } = useQuery({
    queryKey: ["file-signed-url", fileId],
    queryFn: () => fileService.getFileSignedUrl(fileId!),
    enabled: fileId !== null,
    staleTime: 60_000,
  });
  return (
    <Avatar
      size="4"
      src={url}
      fallback={(fallback ?? "?").charAt(0).toUpperCase()}
      radius="full"
    />
  );
}

function AvatarPicker({
  onPick,
  onRemove,
  hasAvatar,
}: {
  onPick: (fileId: number) => void;
  onRemove: () => void;
  hasAvatar: boolean;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Flex gap="2" align="center">
      <Button
        type="button"
        variant="soft"
        size="1"
        onClick={() => setOpen(true)}
      >
        <ImageIcon size={14} /> {hasAvatar ? "Change" : "Pick image"}
      </Button>
      {hasAvatar && (
        <Button
          type="button"
          variant="soft"
          color="red"
          size="1"
          onClick={onRemove}
        >
          <X size={14} /> Remove
        </Button>
      )}
      <FilePickerDialog
        open={open}
        onOpenChange={setOpen}
        onSelect={(file) => onPick(file.id)}
        restrictedType="image"
        title="Pick an avatar"
        description="Choose from an existing image or upload a new one."
      />
    </Flex>
  );
}

function TextInput({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string | null;
  onChange: (v: string | null) => void;
  placeholder?: string;
}) {
  return (
    <Box>
      <Text size="2" weight="medium" color="gray" mb="1" className="block">
        {label}
      </Text>
      <TextField.Root
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
      />
    </Box>
  );
}

function ConsentSelect({
  value,
  onChange,
}: {
  value: boolean | null;
  onChange: (v: boolean | null) => void;
}) {
  const raw = value === null ? "unset" : value ? "given" : "declined";
  return (
    <Box>
      <Text size="2" weight="medium" color="gray" mb="1" className="block">
        Marketing consent
      </Text>
      <Select.Root
        value={raw}
        onValueChange={(v) =>
          onChange(v === "unset" ? null : v === "given")
        }
      >
        <Select.Trigger style={{ width: "100%" }} />
        <Select.Content>
          <Select.Item value="unset">Not asked</Select.Item>
          <Select.Item value="given">Given</Select.Item>
          <Select.Item value="declined">Declined</Select.Item>
        </Select.Content>
      </Select.Root>
    </Box>
  );
}

function Field({
  label,
  value,
  inline = false,
}: {
  label: string;
  value: string | null;
  inline?: boolean;
}) {
  return (
    <Box>
      <Text
        size={inline ? "1" : "2"}
        weight="medium"
        color="gray"
        mb="1"
        className="block"
      >
        {label}
      </Text>
      <Text size="2">{value ?? "—"}</Text>
    </Box>
  );
}

function ReadOnlyGroup({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Box>
      <Text size="2" weight="medium" color="gray" mb="2" className="block">
        {label}{" "}
        <Text size="1" color="gray">
          (read-only)
        </Text>
      </Text>
      <Grid columns={{ initial: "1", sm: "3" }} gap="3">
        {children}
      </Grid>
    </Box>
  );
}

function editableSubset(profile: IUserProfile): UpdateUserProfile {
  return {
    preferred_name: profile.preferred_name,
    avatar_file_id: profile.avatar_file_id,
    timezone: profile.timezone,
    locale: profile.locale,
    marketing_consent: profile.marketing_consent,
  };
}

function populatedFieldSummary(profile: IUserProfile): string {
  const trackedKeys: (keyof IUserProfile)[] = [
    "preferred_name", "avatar_file_id", "timezone", "locale", "marketing_consent",
    "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content",
    "referrer", "signup_ip", "signup_country",
  ];
  const populated = trackedKeys.filter((k) => profile[k] !== null && profile[k] !== undefined).length;
  return populated === 0 ? "No data yet" : `${populated} field${populated === 1 ? "" : "s"} set`;
}

function consentLabel(consent: boolean | null, at: string | null): string | null {
  if (consent === null) return "Not asked";
  if (consent === false) return "Declined";
  return at ? `Given on ${formatDate(at)}` : "Given";
}

